import MultiSelect from "components/ui/MultiSelect";
import type { SelectOption } from "lib/selectOptions";
import type { InstructorFormController } from "../useInstructorForm";
import type { InstructorFormValues } from "../types";

type Props = {
  form: InstructorFormValues;
  isArabic: boolean;
  controller: InstructorFormController;
  onChange: (updater: (previous: InstructorFormValues) => InstructorFormValues) => void;
};

function AssignmentSelect({
  label,
  placeholder,
  value,
  options,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: number[];
  options: SelectOption[];
  onChange: (next: number[]) => void;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{label}</span>
      <MultiSelect
        value={value}
        options={options}
        onChange={(next) => onChange(next.map(Number))}
        placeholder={placeholder}
      />
    </label>
  );
}

export default function InstructorAssignmentsSection({ form, isArabic, controller, onChange }: Props) {
  const {
    optionsLoading,
    stageOptions,
    gradeOptions,
    subjectOptions,
    rawOptions,
  } = controller;

  return (
    <section className="border-t border-gray-200 pt-5 dark:border-white/10">
      <div className="mb-3">
        <h5 className="text-sm font-bold text-navy-700 dark:text-white">
          {isArabic ? "التعيينات الأكاديمية" : "Academic assignments"}
        </h5>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {isArabic
            ? "حدد المرحلة أو الصف أو المواد التي يدرسها المدرس."
            : "Choose the stages, grades, or subjects assigned to the instructor."}
        </p>
      </div>

      {optionsLoading ? (
        <p className="rounded-xl bg-gray-50 px-3 py-4 text-center text-xs text-gray-400 dark:bg-white/5">
          {isArabic ? "جارٍ تحميل القوائم..." : "Loading options..."}
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <AssignmentSelect
            label={isArabic ? "المراحل الدراسية" : "Academic stages"}
            placeholder={isArabic ? "اختر المراحل..." : "Select stages..."}
            value={form.stageIds}
            options={stageOptions}
            onChange={(next) =>
              onChange((previous) => ({
                ...previous,
                stageIds: next,
                gradeIds: previous.gradeIds.filter((id) =>
                  (rawOptions?.grades ?? []).some(
                    (grade) => grade.id === id && next.includes(grade.stage_id)
                  )
                ),
              }))
            }
          />
          <AssignmentSelect
            label={isArabic ? "الصفوف" : "Grades"}
            placeholder={isArabic ? "اختر الصفوف..." : "Select grades..."}
            value={form.gradeIds}
            options={gradeOptions}
            onChange={(next) => onChange((previous) => ({ ...previous, gradeIds: next }))}
          />
          <div className="md:col-span-2">
            <AssignmentSelect
              label={isArabic ? "مواد التعليم الأساسي" : "Basic education subjects"}
              placeholder={isArabic ? "اختر المواد..." : "Select subjects..."}
              value={form.subjectIds}
              options={subjectOptions}
              onChange={(next) => onChange((previous) => ({ ...previous, subjectIds: next }))}
            />
          </div>
        </div>
      )}
    </section>
  );
}
